import { isArray, isBoolean, isNaN } from 'lodash';
import * as yup from 'yup';
import { Venue } from 'store/venues';
import { Kiosk } from 'store/kiosks';
import { Area } from 'store/areas';
import { ClientIntegration } from 'store/clientIntegrations';
import { Configuration } from 'store/configurations';
import { TicketTemplate } from 'store/ticketTemplates';
import { Integration } from 'store/integrations';
import { KioskHardware } from 'store/kioskHardware';
import { Hardware } from 'store/hardware';
import { validateText, validateObject } from 'utils/validation';
import { INVALID } from './constants';
import { PropertyType } from './types';

export const hardwareValidationSchema = yup.object().shape({
  name: validateText(),
  code: validateText()
});

export const kioskValidationSchema = yup.object().shape({
  name: validateText(),
  macAddress: validateText()
});

export const kioskHardwareValidationSchema = yup.object().shape({
  serialCode: validateText(),
  configuration: validateObject(),
  values: validateObject()
});

export const venueValidationSchema = yup.object().shape({
  name: validateText(),
  address: validateText()
});

export const areaValidationSchema = yup.object().shape({
  name: validateText(),
  description: validateText()
});

export const getVenueInitialForm = (venue?: Venue) => ({
  name: venue ? venue.name : '',
  address: venue ? venue.address : ''
});

export const getAreaInitialForm = (area?: Area) => ({
  name: area ? area.name : '',
  description: area ? area.description : '',
  venueId: area ? area.venueId : ''
});

export const getKioskInitialForm = (kiosk?: Kiosk) => ({
  name: kiosk ? kiosk.name : '',
  configurationId: kiosk ? kiosk.configurationId : '',
  clientIntegrationId: kiosk ? kiosk.clientIntegrationId : '',
  areaId: kiosk ? kiosk.areaId : '',
  macAddress: kiosk ? kiosk.macAddress : ''
});

export const getKioskHardwareInitialForm = (kioskHardware?: KioskHardware) => ({
  kioskId: kioskHardware ? kioskHardware.kioskId : '',
  hardwareId: kioskHardware ? kioskHardware.hardwareId : '',
  serialCode: kioskHardware ? kioskHardware.serialCode : '',
  configuration: kioskHardware ? JSON.stringify(kioskHardware.configuration) : '',
  values: kioskHardware ? JSON.stringify(kioskHardware.values) : '',
  status: kioskHardware ? kioskHardware.status : ''
});

export const getHardwareInitialForm = (hardware?: Hardware) => ({
  name: hardware ? hardware.name : '',
  type: hardware ? hardware.type : '',
  code: hardware ? hardware.code : ''
});

export const getCIInitialForm = (clientIntegration?: ClientIntegration) => ({
  name: clientIntegration ? clientIntegration.name : '',
  integrationId: clientIntegration ? clientIntegration.integration.id : '',
  baseUrl: clientIntegration ? clientIntegration.baseUrl : ''
});

export const getIntegrationInitialForm = (integration?: Integration) => ({
  name: integration ? integration.name : '',
  code: integration ? integration.code : ''
});

export const getConfigurationInitialForm = (configuration?: Configuration) => ({
  name: configuration ? configuration.name : ''
});

export const getTicketTemplateInitialForm = (ticketTemplate?: TicketTemplate) => ({
  type: ticketTemplate ? ticketTemplate.type : '',
  template: ticketTemplate ? ticketTemplate.template : ''
});

export const getCustomerInitialForm = () => ({
  request: ''
});

export const getHasEmpty = (values: (string | null | undefined)[]) => {
  return values.some((value) => !value || !value.toString().trim().length);
};

/**
 * Checks to see if any of validation object values has INVALID
 * @param {object} validation - validation object --> has the shape of validationSchema object
 */
export const getHasInvalidInput = (validation: { [x: string]: string }) => {
  const tempValidation = validation;
  return Object.values(tempValidation).some((value) => value === INVALID);
};

export const isValidJSON = (jsonStr: string) => {
  try {
    const obj = JSON.parse(jsonStr);
    if (obj && typeof obj === 'object') {
      return true;
    }
  } catch (err) {
    // DO NOTHING
  }
  return false;
};

export const isValidData = (propertyValue: PropertyType, selectedDataType: string): boolean => {
  if (propertyValue) {
    switch (selectedDataType) {
      case 'object':
        return isValidJSON(propertyValue as string);
      case 'array':
        return isArray(JSON.parse(propertyValue as string));
      case 'number':
        return !isNaN(Number(propertyValue));
      case 'boolean':
        return isBoolean(Boolean(propertyValue));
      case 'string':
      default:
        return true;
    }
  }
  return true;
};

export const getHasEmptyInput = (
  form: ReturnType<
    | typeof getVenueInitialForm
    | typeof getKioskInitialForm
    | typeof getCIInitialForm
    | typeof getConfigurationInitialForm
    | typeof getHardwareInitialForm
    | typeof getKioskHardwareInitialForm
    | typeof getTicketTemplateInitialForm
    | typeof getAreaInitialForm
    | typeof getIntegrationInitialForm
    | typeof getCustomerInitialForm
  >
) => {
  return Object.values(form).some((value) => value === '');
};

export const HardwareTypeList = [
  { name: 'printer', code: 'printer' },
  { name: 'payment device', code: 'paymentDevice' },
  { name: 'barcode scanner', code: 'barcodeScanner' }
];
